import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { LogOut, Loader2, RefreshCw, Building2, Home, Users, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  sold: { label: 'Sold', bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500', border: 'border-red-200' },
  vacant: { label: 'Available', bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500', border: 'border-emerald-200' }
};

const timeAgo = (dateStr) => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

// ── Animated counter (1s smooth ease-out) ──
const useAnimatedCount = (target, duration = 1000) => {
  const [count, setCount] = useState(0);
  const prev = useRef(0);

  useEffect(() => {
    if (target === prev.current) return;
    const start = prev.current;
    const diff = target - start;
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min((now - t0) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3); // cubic ease-out
      setCount(Math.round(start + diff * eased));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
    prev.current = target;
  }, [target, duration]);

  return count;
};

// ── Intersection observer ──
const useInView = (threshold = 0.1) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setInView(true); },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
};

// ── Ripple helper ──
const createRipple = (e) => {
  const btn = e.currentTarget;
  const rect = btn.getBoundingClientRect();
  const size = Math.max(rect.width, rect.height);
  const x = e.clientX - rect.left - size / 2;
  const y = e.clientY - rect.top - size / 2;
  const ripple = document.createElement('span');
  ripple.className = 'ripple-ring';
  ripple.style.width = ripple.style.height = `${size}px`;
  ripple.style.left = `${x}px`;
  ripple.style.top = `${y}px`;
  btn.appendChild(ripple);
  setTimeout(() => ripple.remove(), 500);
};

// ── Stat Card ──
const StatCard = ({ icon: Icon, label, value, color, delay = 0 }) => {
  const animVal = useAnimatedCount(value, 1000);
  const [ref, inView] = useInView();

  return (
    <div
      ref={ref}
      className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white border border-[#E8E8E3] btn-press"
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(12px)',
        transition: `all 0.5s cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`
      }}
      data-testid={`stat-${label.toLowerCase()}`}
    >
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="w-4 h-4 text-white" />
      </div>
      <div>
        <p className="text-xs text-[#999] font-medium uppercase tracking-wide">{label}</p>
        <p className="text-xl font-bold text-[#2D2D2D] tabular-nums">{animVal}</p>
      </div>
    </div>
  );
};

// ── Unit Card — Premium 3D Flip ──
const UnitCard = ({ unit, index, wingIndex = 0, vacantOnFloor = 0, filterKey }) => {
  const config = STATUS_CONFIG[unit.status] || STATUS_CONFIG.vacant;
  const isVacant = unit.status === 'vacant';
  const isLow = isVacant && vacantOnFloor > 0 && vacantOnFloor <= 3;
  const delay = (index % 12) * 35;

  return (
    <div
      data-testid={`unit-card-${unit.unit_no}`}
      className="flip-card filter-card-animate"
      style={{ animationDelay: `${delay}ms` }}
      key={`${unit.id}-${filterKey}`}
    >
      <div className="flip-card-inner">
        {/* ─── FRONT ─── */}
        <div className={`flip-card-front rounded-xl border-2 ${config.bg} ${config.border} ${isVacant ? 'flip-card-vacant' : ''}`}>
          <div className="flex items-start justify-between mb-1.5">
            <span className="text-lg font-bold text-[#2D2D2D] leading-tight">{unit.unit_no}</span>
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold uppercase ${config.text} bg-white/60`}>
              {isLow && <span className="urgency-dot mr-0.5" />}
              <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />
              {config.label}
            </span>
          </div>
          <p className="text-sm text-[#666]">{unit.carpet_area} sq.ft</p>
          <p className="text-sm font-semibold text-[#2D2D2D]">{unit.unit_type}</p>
          {unit.customer_name && (
            <p className="mt-1.5 text-xs text-[#555] truncate border-t border-[#E8E8E3]/60 pt-1.5 font-medium" title={unit.customer_name}>
              {unit.customer_name}
            </p>
          )}
        </div>

        {/* ─── BACK ─── */}
        <div className={`flip-card-back rounded-xl border-2 ${isVacant ? 'bg-emerald-600 border-emerald-700' : 'bg-[#2D2D2D] border-[#1a1a1a]'}`}>
          <div className="text-center">
            <span className="text-2xl font-bold text-white tracking-tight">{unit.unit_no}</span>
            <div className={`mx-auto mt-2 mb-2 w-8 h-0.5 rounded-full ${isVacant ? 'bg-white/40' : 'bg-white/20'}`} />
          </div>
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between">
              <span className="text-white/60">Area</span>
              <span className="text-white font-semibold">{unit.carpet_area} sq.ft</span>
            </div>
            <div className="flex justify-between">
              <span className="text-white/60">Type</span>
              <span className="text-white font-semibold">{unit.unit_type}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-white/60">Status</span>
              <span className={`font-semibold ${isVacant ? 'text-emerald-200' : 'text-red-300'}`}>{config.label}</span>
            </div>
            {unit.customer_name && (
              <div className="pt-1.5 border-t border-white/10">
                <span className="text-white/50 text-xs">Buyer</span>
                <p className="text-white text-xs font-medium truncate" title={unit.customer_name}>{unit.customer_name}</p>
              </div>
            )}
          </div>
          {isVacant && isLow && (
            <div className="mt-2 text-center">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/15 text-[10px] font-bold text-amber-200 uppercase tracking-wider">
                <span className="urgency-dot-light" />
                Only {vacantOnFloor} left
              </span>
            </div>
          )}
          {isVacant && !isLow && (
            <div className="mt-2 text-center">
              <span className="text-[11px] text-emerald-200 font-medium">Enquire now</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Floor Section ──
const FloorSection = ({ floor, units, floorIndex, wingIndex = 0, allUnitsOnFloor, filterKey }) => {
  const [ref, inView] = useInView(0.05);
  const vacantOnFloor = allUnitsOnFloor.filter(u => u.status === 'vacant').length;
  const isLowFloor = vacantOnFloor > 0 && vacantOnFloor <= 3;

  return (
    <div
      ref={ref}
      className="mb-5 last:mb-0"
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateX(0)' : `translateX(${wingIndex % 2 === 0 ? '-20' : '20'}px)`,
        transition: `all 0.5s cubic-bezier(0.22, 1, 0.36, 1) ${floorIndex * 80}ms`
      }}
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="flex items-center gap-2 bg-[#2D2D2D] text-white px-3 py-1 rounded-lg">
          <Building2 className="w-3.5 h-3.5 opacity-70" />
          <span className="text-sm font-semibold tracking-wide">{floor} Floor</span>
        </div>
        {isLowFloor && (
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-[11px] font-semibold text-amber-700">
            <span className="urgency-dot" />
            {vacantOnFloor} left
          </span>
        )}
        <div className="flex-1 floor-divider" />
        <span className="text-xs text-[#999] font-medium">{units.length} units</span>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
        {units
          .sort((a, b) => a.unit_no.localeCompare(b.unit_no))
          .map((unit, i) => (
            <UnitCard
              key={unit.id}
              unit={unit}
              index={i + floorIndex * 6}
              wingIndex={wingIndex}
              vacantOnFloor={vacantOnFloor}
              filterKey={filterKey}
            />
          ))}
      </div>
    </div>
  );
};

// ═════════════════════════════════════════
// Dashboard
// ═════════════════════════════════════════
const DashboardPage = () => {
  const { user, loading, logout } = useAuth();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [units, setUnits] = useState([]);
  const [stats, setStats] = useState(null);
  const [activeFilters, setActiveFilters] = useState(['sold', 'vacant']);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [, setTick] = useState(0);
  const [filterKey, setFilterKey] = useState(0); // triggers re-animation on filter change
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { if (user) { fetchProjects(); fetchSyncStatus(); } }, [user]);

  useEffect(() => {
    if (selectedProject) { fetchUnits(); fetchStats(); }
  }, [selectedProject]);

  useEffect(() => {
    if (!selectedProject) return;
    const iv = setInterval(() => { fetchUnits(); fetchStats(); fetchSyncStatus(); }, 60000);
    return () => clearInterval(iv);
  }, [selectedProject]);

  useEffect(() => {
    const t = setInterval(() => setTick(n => n + 1), 30000);
    return () => clearInterval(t);
  }, []);

  const fetchProjects = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/projects`);
      setProjects(r.data);
      if (r.data.length > 0) setSelectedProject(r.data[0]);
    } catch (e) { console.error('Error fetching projects:', e); }
  };

  const fetchUnits = async () => {
    if (!selectedProject) return;
    setLoadingUnits(true);
    try {
      const r = await axios.get(`${API_URL}/api/projects/${selectedProject.slug}/units`);
      setUnits(r.data);
    } catch (e) { console.error('Error fetching units:', e); }
    finally { setLoadingUnits(false); }
  };

  const fetchStats = async () => {
    if (!selectedProject) return;
    try {
      const r = await axios.get(`${API_URL}/api/projects/${selectedProject.slug}/stats`);
      setStats(r.data);
    } catch (e) { console.error('Error fetching stats:', e); }
  };

  const fetchSyncStatus = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/sync/status`);
      if (r.data.last_sync) setLastSync(r.data.last_sync.synced_at);
    } catch (e) { console.error('Error fetching sync status:', e); }
  };

  const handleSync = async (e) => {
    createRipple(e);
    setSyncing(true);
    try {
      await axios.post(`${API_URL}/api/sync`, {});
      await fetchUnits(); await fetchStats(); await fetchSyncStatus();
    } catch (e) { console.error('Sync failed:', e); }
    finally { setSyncing(false); }
  };

  const toggleFilter = (status, e) => {
    createRipple(e);
    setActiveFilters(prev => {
      if (prev.includes(status)) {
        if (prev.length > 1) return prev.filter(f => f !== status);
        return prev;
      }
      return [...prev, status];
    });
    setFilterKey(k => k + 1); // re-trigger card animations
  };

  const filteredUnits = units.filter(u => activeFilters.includes(u.status));
  const hasWings = selectedProject?.wings?.length > 0;

  // Group all units (unfiltered) by wing+floor for vacancy count
  const allGrouped = useMemo(() => {
    return units.reduce((acc, u) => {
      const wk = hasWings ? u.wing : 'ALL';
      const fk = u.floor;
      const key = `${wk}-${fk}`;
      if (!acc[key]) acc[key] = [];
      acc[key].push(u);
      return acc;
    }, {});
  }, [units, hasWings]);

  const groupedData = filteredUnits.reduce((acc, u) => {
    const wk = hasWings ? u.wing : 'ALL';
    if (!acc[wk]) acc[wk] = {};
    if (!acc[wk][u.floor]) acc[wk][u.floor] = [];
    acc[wk][u.floor].push(u);
    return acc;
  }, {});

  const floorOrder = ['7TH', '6TH', '5TH', '4TH', '3RD', '2ND', '1ST'];
  const wingOrder = hasWings ? ['A', 'B'] : ['ALL'];
  const soldPct = stats ? Math.round((stats.sold / stats.total) * 100) : 0;
  const vacantPct = stats ? Math.round((stats.vacant / stats.total) * 100) : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F0] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#666] animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="min-h-screen bg-[#F5F5F0]">
      {/* Header — shrinks + glass on scroll */}
      <header
        className={`sticky top-0 z-50 transition-all duration-500 ease-out ${
          scrolled
            ? 'bg-white/70 backdrop-blur-xl border-b border-[#E8E8E3]/60 shadow-sm'
            : 'bg-white border-b border-[#E8E8E3]'
        }`}
      >
        <div className={`max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between transition-all duration-500 ease-out ${
          scrolled ? 'py-1.5' : 'py-3'
        }`}>
          <img
            src="/nova-logo.png"
            alt="Nova Homes"
            className="object-contain transition-all duration-500 ease-out"
            style={{ height: scrolled ? '36px' : '100px', width: 'auto' }}
            data-testid="nova-logo"
          />
          <div className="flex items-center gap-3">
            {user.role === 'admin' && (
              <Button
                onClick={handleSync}
                disabled={syncing}
                variant="outline"
                size="sm"
                data-testid="sync-btn"
                className="btn-press text-[#666] hover:text-[#2D2D2D] gap-2"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${syncing ? 'sync-spin' : ''}`} />
                <span className="hidden sm:inline">{syncing ? 'Syncing...' : 'Sync'}</span>
              </Button>
            )}
            <span className={`text-sm text-[#666] hidden sm:block transition-opacity duration-300 ${scrolled ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'}`}>{user.email}</span>
            <Button onClick={logout} variant="ghost" size="sm" data-testid="logout-btn" className="text-[#666] hover:text-[#2D2D2D]">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-5">
        {/* Project Tabs */}
        <div className="flex gap-2 mb-5 animate-slide-up" style={{ animationDelay: '0.1s' }}>
          {projects.map((project, i) => (
            <button
              key={project.id}
              onClick={(e) => { createRipple(e); setSelectedProject(project); }}
              data-testid={`project-tab-${project.slug}`}
              className={`btn-press flex items-center gap-2.5 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 ${
                selectedProject?.slug === project.slug
                  ? 'bg-[#2D2D2D] text-white shadow-lg shadow-[#2D2D2D]/20'
                  : 'bg-white text-[#666] hover:bg-[#E8E8E3] border border-[#E8E8E3]'
              }`}
              style={{
                animationName: 'slideUp',
                animationDuration: '0.4s',
                animationTimingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)',
                animationFillMode: 'both',
                animationDelay: `${i * 80 + 100}ms`
              }}
            >
              <img src={project.logo_url} alt="" className="w-7 h-7 object-contain rounded" />
              {project.name}
            </button>
          ))}
        </div>

        {/* Hero */}
        {selectedProject && (
          <div className="relative h-52 rounded-2xl overflow-hidden mb-5 animate-hero" key={selectedProject.slug} data-testid="project-hero">
            <img src={selectedProject.elevation_url} alt={selectedProject.name} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-5">
              <div className="flex items-end gap-4">
                <img src={selectedProject.logo_url} alt="" className="w-14 h-14 object-contain bg-white/95 rounded-xl p-1.5 shadow-lg" />
                <div className="animate-slide-up" style={{ animationDelay: '0.2s' }}>
                  <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">{selectedProject.name}</h1>
                  <p className="text-white/75 text-sm mt-0.5">{selectedProject.description}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stats Row */}
        {stats && (
          <div className="grid grid-cols-3 gap-3 mb-5">
            <StatCard icon={Home} label="Total" value={stats.total} color="bg-[#2D2D2D]" delay={0} />
            <StatCard icon={Home} label="Available" value={stats.vacant} color="bg-emerald-500" delay={80} />
            <StatCard icon={Users} label="Sold" value={stats.sold} color="bg-red-500" delay={160} />
          </div>
        )}

        {/* Progress Bar + Filters */}
        <div className="bg-white rounded-2xl border border-[#E8E8E3] p-4 mb-5 animate-slide-up" style={{ animationDelay: '0.3s' }}>
          {stats && stats.total > 0 && (
            <div className="mb-4">
              <div className="flex items-center justify-between text-xs text-[#999] mb-2 font-medium">
                <span>Occupancy</span>
                <span>{soldPct}% Sold</span>
              </div>
              <div className="h-2.5 bg-[#F5F5F0] rounded-full overflow-hidden flex">
                <div className="progress-bar bg-red-400 rounded-l-full" style={{ width: `${soldPct}%` }} />
                <div className="progress-bar bg-emerald-400 rounded-r-full" style={{ width: `${vacantPct}%`, animationDelay: '0.5s' }} />
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="text-sm text-[#999] font-medium">Filter:</span>
              {Object.entries(STATUS_CONFIG).map(([status, config]) => (
                <button
                  key={status}
                  onClick={(e) => toggleFilter(status, e)}
                  data-testid={`filter-${status}`}
                  className={`btn-press px-4 py-1.5 rounded-lg text-sm font-semibold border transition-all duration-300 ${
                    activeFilters.includes(status)
                      ? `${config.bg} ${config.text} ${config.border} shadow-sm`
                      : 'bg-[#F5F5F0] text-[#999] border-transparent hover:border-[#E8E8E3]'
                  }`}
                >
                  <span className="flex items-center gap-1.5">
                    <span className={`w-2 h-2 rounded-full transition-colors duration-300 ${activeFilters.includes(status) ? config.dot : 'bg-[#ccc]'}`} />
                    {config.label}
                  </span>
                </button>
              ))}
            </div>

            {lastSync && (
              <div className="flex items-center gap-2" data-testid="last-sync-time">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                  </span>
                  <span className="text-xs font-semibold text-emerald-700">Live</span>
                </span>
                <span className="text-xs text-[#bbb]">Updated {timeAgo(lastSync)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Inventory Grid */}
        {loadingUnits ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 text-[#999] animate-spin" />
          </div>
        ) : (
          <div className="space-y-5" key={filterKey}>
            {wingOrder.filter(w => groupedData[w]).map((wing, wingIdx) => (
              <div key={wing} className="bg-white rounded-2xl border border-[#E8E8E3] overflow-hidden" data-testid={`wing-section-${wing}`}>
                {hasWings && (
                  <div
                    className="px-5 py-3.5 bg-gradient-to-r from-[#FAFAF8] to-white border-b border-[#E8E8E3]"
                    style={{
                      animationName: wingIdx === 0 ? 'slideInLeft' : 'slideInRight',
                      animationDuration: '0.5s',
                      animationTimingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)',
                      animationFillMode: 'both',
                      animationDelay: `${wingIdx * 150}ms`
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-[#2D2D2D] text-white flex items-center justify-center text-sm font-bold">{wing}</div>
                      <div>
                        <h2 className="text-base font-bold text-[#2D2D2D]">Wing {wing}</h2>
                        <p className="text-xs text-[#999]">{Object.values(groupedData[wing]).flat().length} units</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="p-5">
                  {floorOrder.filter(f => groupedData[wing]?.[f]).map((floor, floorIdx) => (
                    <FloorSection
                      key={floor}
                      floor={floor}
                      units={groupedData[wing][floor]}
                      floorIndex={floorIdx}
                      wingIndex={wingIdx}
                      allUnitsOnFloor={allGrouped[`${wing}-${floor}`] || []}
                      filterKey={filterKey}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {filteredUnits.length === 0 && !loadingUnits && (
          <div className="text-center py-20 text-[#999] animate-slide-up">
            <Home className="w-12 h-12 mx-auto mb-3 text-[#ddd]" />
            <p className="text-base font-medium">No units match selected filters</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default DashboardPage;
